import React, { useState, useMemo } from "react";
import {
    Box,
    Stack,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    Container,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PublicationCard from "./PublicationCard";
import publications from "../../info/publications";
import { singlePage } from "../../info/Info";
import { Helmet } from "react-helmet-async";

export default function Portfolio({ innerRef }) {
    const [selectedCategory, setSelectedCategory] = useState("selected");
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState("cards"); // "cards" or "compact"
    const [copiedIndex, setCopiedIndex] = useState(null);

    const categories = [
        { id: "selected", label: "Selected Highlights", count: publications.filter((p) => p.selected).length },
        { id: "reasoning", label: "Reasoning & LLMs", count: publications.filter((p) => p.category === "reasoning").length },
        { id: "3d-gen", label: "3D/4D Generative AI", count: publications.filter((p) => p.category === "3d-gen").length },
        { id: "neural-rendering", label: "Computer Vision", count: publications.filter((p) => p.category === "neural-rendering" || p.category === "vision").length },
        { id: "all", label: "All", count: publications.length }
    ];

    const filteredPublications = useMemo(() => {
        return publications.filter((pub) => {
            let matchesCategory = true;
            if (selectedCategory === "selected") {
                matchesCategory = Boolean(pub.selected);
            } else if (selectedCategory === "neural-rendering") {
                matchesCategory = pub.category === "neural-rendering" || pub.category === "vision";
            } else if (selectedCategory !== "all") {
                matchesCategory = pub.category === selectedCategory;
            }

            if (!matchesCategory) return false;

            if (!searchQuery.trim()) return true;

            const query = searchQuery.toLowerCase();
            const titleMatch = pub.title.toLowerCase().includes(query);
            const authorMatch = pub.authors.some((a) => a.toLowerCase().includes(query));
            const confMatch = pub.conference.toLowerCase().includes(query);
            const yearMatch = pub.year ? pub.year.toString().includes(query) : false;

            return titleMatch || authorMatch || confMatch || yearMatch;
        });
    }, [selectedCategory, searchQuery]);

    const handleCopyBibtex = (bibtex, index) => {
        if (bibtex) {
            navigator.clipboard.writeText(bibtex);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        }
    };

    return (
        <>
            {!singlePage && (
                <Helmet>
                    <title>Selected Publications | Filippos Kokkinos</title>
                    <meta
                        name="description"
                        content="Selected publications and research papers by Filippos Kokkinos, AI Research Scientist."
                    />
                </Helmet>
            )}
            <Container maxWidth="md" sx={{ py: 4 }} ref={innerRef} id="portfolio">
                <Box textAlign="center" mb={3.5}>
                    <Typography
                        variant="h4"
                        component="h1"
                        fontWeight={700}
                        gutterBottom
                        sx={{
                            background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            display: "inline-block"
                        }}
                    >
                        Selected Publications
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: "auto", mt: 0.5 }}>
                        Frontier research across Large Multimodal Foundation Models, Reasoning, 3D/4D Generative AI, and Neural Rendering.
                    </Typography>

                    {/* Category Filter Pills */}
                    <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} mt={2.5}>
                        {categories.map((cat) => (
                            <Chip
                                key={cat.id}
                                label={`${cat.label} (${cat.count})`}
                                onClick={() => setSelectedCategory(cat.id)}
                                variant={selectedCategory === cat.id ? "filled" : "outlined"}
                                color={selectedCategory === cat.id ? "primary" : "default"}
                                sx={{
                                    fontWeight: selectedCategory === cat.id ? 600 : 400,
                                    borderRadius: "16px",
                                    px: 0.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        transform: "translateY(-1px)"
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {/* Search & View Mode Switcher Toolbar */}
                    <Box
                        mt={2.5}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1.5}
                    >
                        <TextField
                            size="small"
                            placeholder="Filter by keyword, author, venue, year..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: 320 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: "20px", fontSize: "0.9rem" }
                            }}
                        />

                        {/* View Mode Switcher */}
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, nextView) => {
                                if (nextView !== null) setViewMode(nextView);
                            }}
                            size="small"
                            sx={{ borderRadius: "10px" }}
                        >
                            <ToggleButton value="cards" title="Visual Cards View" sx={{ px: 1.5, textTransform: "none", fontSize: "0.8rem", gap: 0.5 }}>
                                <ViewModuleIcon fontSize="small" /> Cards
                            </ToggleButton>
                            <ToggleButton value="compact" title="Compact List View" sx={{ px: 1.5, textTransform: "none", fontSize: "0.8rem", gap: 0.5 }}>
                                <ViewHeadlineIcon fontSize="small" /> Compact
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {/* Publications Render: Cards View or Compact View */}
                {filteredPublications.length > 0 ? (
                    viewMode === "cards" ? (
                        <Stack spacing={2.5}>
                            {filteredPublications.map((publication, index) => (
                                <PublicationCard
                                    key={index}
                                    image={publication.image}
                                    video={publication.video}
                                    title={publication.title}
                                    authors={publication.authors}
                                    projectPage={publication.projectPage}
                                    arxivLink={publication.arxivLink}
                                    conference={publication.conference}
                                    mediaLink={publication.mediaLink}
                                    year={publication.year}
                                    bibtex={publication.bibtex}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 3 },
                                borderRadius: "14px",
                                border: "1px solid",
                                borderColor: "divider",
                                background: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "rgba(30, 41, 59, 0.5)"
                                        : "rgba(255, 255, 255, 0.85)"
                            }}
                        >
                            <Stack spacing={2}>
                                {filteredPublications.map((pub, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            pb: index < filteredPublications.length - 1 ? 2 : 0,
                                            borderBottom:
                                                index < filteredPublications.length - 1
                                                    ? "1px dashed"
                                                    : "none",
                                            borderColor: "divider"
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            <Chip
                                                label={pub.conference}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.72rem",
                                                    height: 22,
                                                    borderRadius: "6px"
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            lineHeight={1.35}
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": { color: "primary.main" }
                                            }}
                                            onClick={() => window.open(pub.projectPage || pub.arxivLink, "_blank")}
                                        >
                                            {pub.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5, lineHeight: 1.45 }}
                                        >
                                            {pub.authors.map((author, aIdx) => {
                                                const isFilippos = author.includes("Filippos Kokkinos");
                                                return (
                                                    <span
                                                        key={aIdx}
                                                        style={{
                                                            fontWeight: isFilippos ? 700 : 400,
                                                            color: isFilippos ? "inherit" : undefined
                                                        }}
                                                    >
                                                        {author}
                                                        {aIdx < pub.authors.length - 1 && ", "}
                                                    </span>
                                                );
                                            })}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                                            {pub.projectPage && pub.projectPage !== pub.arxivLink && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    href={pub.projectPage}
                                                    target="_blank"
                                                    rel="noopener"
                                                    endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
                                                    sx={{
                                                        textTransform: "none",
                                                        borderRadius: "6px",
                                                        fontSize: "0.72rem",
                                                        py: 0.2,
                                                        px: 1
                                                    }}
                                                >
                                                    Project Page
                                                </Button>
                                            )}
                                            {pub.arxivLink && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    href={pub.arxivLink}
                                                    target="_blank"
                                                    rel="noopener"
                                                    endIcon={<LaunchIcon sx={{ fontSize: 12 }} />}
                                                    sx={{
                                                        textTransform: "none",
                                                        borderRadius: "6px",
                                                        fontSize: "0.72rem",
                                                        py: 0.2,
                                                        px: 1
                                                    }}
                                                >
                                                    arXiv
                                                </Button>
                                            )}
                                            {pub.bibtex && (
                                                <Tooltip title={copiedIndex === index ? "Copied!" : "Copy BibTeX"}>
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => handleCopyBibtex(pub.bibtex, index)}
                                                        startIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontSize: "0.72rem",
                                                            py: 0.2,
                                                            px: 0.8
                                                        }}
                                                    >
                                                        {copiedIndex === index ? "Copied!" : "BibTeX"}
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    )
                ) : (
                    <Box textAlign="center" py={6}>
                        <Typography variant="body1" color="text.secondary">
                            No publications matched your search query.
                        </Typography>
                    </Box>
                )}

                {/* Show All Publications Button when in Selected mode */}
                {selectedCategory === "selected" && !searchQuery && (
                    <Box textAlign="center" mt={4}>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => setSelectedCategory("all")}
                            sx={{
                                textTransform: "none",
                                borderRadius: "10px",
                                px: 3,
                                py: 1,
                                fontWeight: 600
                            }}
                        >
                            View All 29 Publications
                        </Button>
                    </Box>
                )}
            </Container>
        </>
    );
}
